import { httpClient } from "@bw/shared/api";
import type { Schema } from "@bw/shared/types/generated";

export interface ListReportsParams {
  status?: "pending" | "resolved";
  limit?: number;
  offset?: number;
}

export interface ListAppealsParams {
  status?: "pending" | "upheld" | "lifted" | "inactive";
  limit?: number;
  offset?: number;
}

export async function listReports(
  params?: ListReportsParams,
): Promise<Schema["PlatformReportListResponse"]> {
  const search = new URLSearchParams();
  if (params?.status) search.set("status", params.status);
  if (params?.limit !== undefined) search.set("limit", String(params.limit));
  if (params?.offset !== undefined) search.set("offset", String(params.offset));
  const query = search.toString();
  return httpClient.get<Schema["PlatformReportListResponse"]>(
    `/platform/moderation/reports${query ? `?${query}` : ""}`,
  );
}

export async function resolveReport(
  reportId: string,
  disposition: Schema["ResolveReportRequest"]["disposition"],
  targetRevision: string | null,
  notes?: string | null,
): Promise<Schema["ReportResolutionResponse"]> {
  const payload = {
    disposition,
    target_revision: targetRevision,
    notes,
  } satisfies Schema["ResolveReportRequest"];
  return httpClient.post<Schema["ReportResolutionResponse"]>(
    `/moderation/reports/${encodeURIComponent(reportId)}/resolve`,
    payload,
  );
}

export async function getReport(
  reportId: string,
): Promise<Schema["PlatformReportDetailResponse"]> {
  return httpClient.get<Schema["PlatformReportDetailResponse"]>(
    `/platform/moderation/reports/${encodeURIComponent(reportId)}`,
  );
}

export async function listAppeals(
  params?: ListAppealsParams,
): Promise<Schema["PlatformAppealListResponse"]> {
  const search = new URLSearchParams();
  if (params?.status) search.set("status", params.status);
  if (params?.limit !== undefined) search.set("limit", String(params.limit));
  if (params?.offset !== undefined) search.set("offset", String(params.offset));
  const query = search.toString();
  return httpClient.get<Schema["PlatformAppealListResponse"]>(
    `/platform/moderation/appeals${query ? `?${query}` : ""}`,
  );
}

export async function getAppeal(
  appealId: string,
): Promise<Schema["PlatformAppealDetailResponse"]> {
  return httpClient.get<Schema["PlatformAppealDetailResponse"]>(
    `/platform/moderation/appeals/${encodeURIComponent(appealId)}`,
  );
}

export async function resolveAppeal(
  appealId: string,
  decision: Schema["AppealResolutionRequest"]["decision"],
): Promise<Schema["AppealResolutionResponse"]> {
  const payload = { decision } satisfies Schema["AppealResolutionRequest"];
  return httpClient.put<Schema["AppealResolutionResponse"]>(
    `/platform/moderation/appeals/${encodeURIComponent(appealId)}/resolve`,
    payload,
  );
}

export interface ListBansParams {
  active_only?: boolean;
  limit?: number;
  offset?: number;
}

export async function listBans(
  params?: ListBansParams,
): Promise<Schema["PlatformBanListResponse"]> {
  const search = new URLSearchParams();
  if (params?.active_only !== undefined) {
    search.set("active_only", String(params.active_only));
  }
  if (params?.limit !== undefined) search.set("limit", String(params.limit));
  if (params?.offset !== undefined) search.set("offset", String(params.offset));
  const query = search.toString();
  return httpClient.get<Schema["PlatformBanListResponse"]>(
    `/platform/moderation/bans${query ? `?${query}` : ""}`,
  );
}

export async function issueBan(
  userId: string,
  reason: string,
  expiresAt?: string | null,
): Promise<Schema["BanResponse"]> {
  const payload = {
    user_id: userId,
    reason,
    ...(expiresAt ? { expires_at: expiresAt } : {}),
  } satisfies Schema["BanUserRequest"];
  return httpClient.post<Schema["BanResponse"]>("/moderation/bans", payload);
}

export async function unbanUser(userId: string): Promise<void> {
  const payload = { user_id: userId } satisfies Schema["UnbanUserRequest"];
  await httpClient.post("/moderation/bans/unban", payload);
}
